import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./auth/AuthContext";
import { I18nProvider } from "./i18n/I18nContext";
import { BrandingProvider } from "./branding/BrandingContext";
import { ProtectedRoute } from "./auth/ProtectedRoute";
import { ConfirmProvider } from "./hooks/useConfirm";
import { Toaster } from "sonner";
import Dashboard from "./pages/Dashboard";
import MobileHome from "./pages/MobileHome";
import MarketingLanding from "./pages/MarketingLanding";
import { PUBLIC_ROUTES, PROTECTED_ROUTES, ROUTE_ALIASES } from "./routes";
import PortalLogin from "./pages/portal/PortalLogin";
import PortalLayout from "./pages/portal/PortalLayout";
import PortalDashboard from "./pages/portal/PortalDashboard";
import PortalAgenda from "./pages/portal/PortalAgenda";
import PortalReceipts from "./pages/portal/PortalReceipts";
import PortalEstimates from "./pages/portal/PortalEstimates";
import PortalBooking from "./pages/portal/PortalBooking";
import CollaboratorInvite from "./pages/CollaboratorInvite";
import TalentPortalLogin from "./pages/talent-portal/TalentPortalLogin";
import TalentPortalDashboard from "./pages/talent-portal/TalentPortalDashboard";
import PublicGallery from "./pages/PublicGallery";
import PlatformLogin from "./pages/PlatformLogin";
import { PWAInstallPrompt } from "./components/PWAInstallPrompt";
import { ScrollToTop } from "./components/ScrollToTop";

/**
 * Returns true when the app is being served on the platform-admin host
 * (e.g. https://admin.cleanmanagerusa.com). In that case we hide the entire
 * CleanManager surface and only expose the Super Admin portal.
 *
 * Hosts are matched against:
 *   - any hostname starting with "admin." (default convention)
 *   - any value listed in REACT_APP_PLATFORM_HOSTS (comma-separated)
 */
function isPlatformHost() {
  if (typeof window === "undefined") return false;
  const host = (window.location.hostname || "").toLowerCase();
  if (host.startsWith("admin.")) return true;
  const list = (process.env.REACT_APP_PLATFORM_HOSTS || "")
    .split(",")
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
  return list.includes(host);
}

function RoleHome() {
  const { user } = useAuth();
  if (user === null)
    return (
      <div className="h-screen flex items-center justify-center text-slate-500">
        Carregando…
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  return user.role === "collaborator" ? <MobileHome /> : <Dashboard />;
}

function RootRoute() {
  // A home pública ('/') mostra a landing de marketing (HTML do cliente).
  // O acesso ao painel é explícito via "Entrar"/"Começar grátis" → /login e /signup.
  return <MarketingLanding />;
}

/**
 * Routes mounted when the app is hosted on the platform-admin subdomain.
 * Anything that isn't the platform login or a /super-admin/* route is
 * redirected to /platform/login — CleanManager simply doesn't exist here.
 */
function PlatformOnlyRoutes() {
  return (
    <Routes>
      <Route path="/platform/login" element={<PlatformLogin />} />
      {PROTECTED_ROUTES.filter((r) => r.path.startsWith("/super-admin")).map(
        ({ path, element: Element, roles, menuKey }) => (
          <Route
            key={path}
            path={path}
            element={
              <ProtectedRoute roles={roles} menuKey={menuKey}>
                <Element />
              </ProtectedRoute>
            }
          />
        ),
      )}
      <Route path="/" element={<Navigate to="/platform/login" replace />} />
      <Route path="*" element={<Navigate to="/platform/login" replace />} />
    </Routes>
  );
}

export default function App() {
  const platformHost = isPlatformHost();
  return (
    <AuthProvider>
      <I18nProvider>
      <BrandingProvider>
        <ConfirmProvider>
        <BrowserRouter>
          <ScrollToTop />
          <Toaster position="top-center" richColors />
          {platformHost ? (
            <PlatformOnlyRoutes />
          ) : (
          <Routes>
            {PUBLIC_ROUTES.map(({ path, element: Element }) => (
              <Route key={path} path={path} element={<Element />} />
            ))}

            {ROUTE_ALIASES.map(({ from, to }) => (
              <Route key={from} path={from} element={<Navigate to={to} replace />} />
            ))}

            {/* Portal do Cliente — rotas isoladas com layout próprio */}
            <Route path="/portal/login" element={<PortalLogin />} />
            <Route path="/portal" element={<PortalLayout />}>
              <Route index element={<PortalDashboard />} />
              <Route path="agendar" element={<PortalBooking />} />
              <Route path="agenda" element={<PortalAgenda />} />
              <Route path="recibos" element={<PortalReceipts />} />
              <Route path="orcamentos" element={<PortalEstimates />} />
            </Route>

            {/* Convite público de colaborador (sem auth) */}
            <Route path="/invite/:token" element={<CollaboratorInvite />} />

            {/* Portal do Colaborador (Banco de Colaboradores) — token próprio */}
            <Route path="/portal-colaborador" element={<TalentPortalLogin />} />
            <Route path="/portal-colaborador/painel" element={<TalentPortalDashboard />} />

            {/* Portal exclusivo Super Admin (Platform Admin) — login isolado */}
            <Route path="/platform/login" element={<PlatformLogin />} />

            {/* Galeria pública de fotos antes/depois (acessada via QR Code do recibo) */}
            <Route path="/g/:jobId" element={<PublicGallery />} />

            <Route
              path="/"
              element={<RootRoute />}
            />

            {/* Painel do app (dashboard admin / app do colaborador) — acesso explícito */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <RoleHome />
                </ProtectedRoute>
              }
            />

            {PROTECTED_ROUTES.map(({ path, element: Element, roles, menuKey, superAdminOnly }) => (
              <Route
                key={path}
                path={path}
                element={
                  <ProtectedRoute roles={roles} menuKey={menuKey} superAdminOnly={superAdminOnly}>
                    <Element />
                  </ProtectedRoute>
                }
              />
            ))}

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          )}
          {!platformHost && <PWAInstallPrompt />}
        </BrowserRouter>
      </ConfirmProvider>
      </BrandingProvider>
      </I18nProvider>
    </AuthProvider>
  );
}
